import { useState, useMemo } from 'react';
import { Contact } from '../types/Contact';

type SortConfig = {
  key: keyof Contact;
  direction: 'ascending' | 'descending';
};

function sortData(contacts: Contact[], config: SortConfig): Contact[] {
  return [...contacts].sort((a, b) => {
    const key = config.key;
    const aValue = a[key] ?? '';
    const bValue = b[key] ?? '';

    // Handle numeric fields
    if (key === 'conversations_count') {
      const aNum = Number(aValue) || 0;
      const bNum = Number(bValue) || 0;
      if (aNum < bNum) {
        return config.direction === 'ascending' ? -1 : 1;
      }
      if (aNum > bNum) {
        return config.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    }

    // Handle date fields
    if (key === 'created_at' || key === 'last_conversation_at') {
      const aDate = new Date(aValue || 0);
      const bDate = new Date(bValue || 0);
      if (aDate < bDate) {
        return config.direction === 'ascending' ? -1 : 1;
      }
      if (aDate > bDate) {
        return config.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    }

    // Handle string fields
    if (aValue < bValue) {
      return config.direction === 'ascending' ? -1 : 1;
    }
    if (aValue > bValue) {
      return config.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });
}

function getNextSortDirection(
  current: 'ascending' | 'descending' | undefined
): 'ascending' | 'descending' {
  if (current === 'ascending') return 'descending';
  return 'ascending';
}

export function useContactSort(contacts: Contact[]) {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const sortedContacts = useMemo(() => {
    if (sortConfig !== null) {
      return sortData(contacts, sortConfig);
    }
    return contacts;
  }, [contacts, sortConfig]);

  const requestSort = (key: keyof Contact) => {
    const direction =
      sortConfig && sortConfig.key === key
        ? getNextSortDirection(sortConfig.direction)
        : 'ascending';
    setSortConfig({ key, direction });
  };

  return { sortedContacts, requestSort, sortConfig };
}
